import { useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLocale } from '@/contexts/LocaleContext';
import { useUserContext } from '@/contexts/UserContext';
import { useRouteEntitySafe } from '@/contexts/RouteEntityContext';
import i18n from '@/i18n/config';
import { logger } from '@/utils/logger';
import { INTERNATIONAL_LOCATION_ID } from '@/constants/config';
import { routeTo } from '@/lib/routeHelpers';
import { usePrimaryLocale } from './usePrimaryLocale';
import { fetchTranslatedSlug } from './useTranslatedSlug';

/**
 * Unified Localization Hook
 * 
 * Single source of truth for all language-aware operations.
 * Uses DB-first routing for path resolution.
 * 
 * Features:
 * - DB-backed path generation (via useTranslatedSlug)
 * - Location-aware path generation
 * - Race-condition-safe language changing
 * - User's selected language used for all paths
 */
export const useLocalization = () => {
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const { settings, updateSettingsWithoutI18n, formatDate, formatNumber, formatCurrency } = useLocale();
  const { currentLocation, intentMode } = useUserContext();
  const { resolveToPrimary: resolveToPrimaryLocale } = usePrimaryLocale();
  
  // Route entity context for identity-based language switching (INV-4)
  const routeEntityContext = useRouteEntitySafe();

  // Use database-driven primary locale resolution (handles zh-TW correctly)
  const baseLanguage = useMemo(() => resolveToPrimaryLocale(settings.locale), [settings.locale, resolveToPrimaryLocale]);

  // Location slug - not needed for DB-first architecture
  const locationSlug = useMemo(() => {
    if (!currentLocation || currentLocation === INTERNATIONAL_LOCATION_ID || intentMode === 'international') {
      return null;
    }
    // In DB-first architecture, location slugs are resolved per-request
    return null;
  }, [currentLocation, intentMode]);

  // Location language - simplified
  const locationLanguage: string | null = null;

  /**
   * Get the homepage path (logo destination)
   */
  const getHomePath = useCallback((): string => {
    return '/';
  }, []);

  /**
   * @deprecated Use useLocalizedRoutes().getPath() instead for localized route paths.
   * This sync version only returns the raw route key.
   */
  const getPath = useCallback((routeKey: string): string => {
    if (process.env.NODE_ENV === 'development') {
      logger.warn('[useLocalization] getPath is deprecated - use useLocalizedRoutes().getPath() instead');
    }
    return `/${routeKey}`;
  }, []);

  /**
   * Get localized route slug for a route key
   */
  const getLocalizedRouteSlug = useCallback(async (routeKey: string): Promise<string> => {
    const slug = await fetchTranslatedSlug('route', routeKey, settings.locale);
    return slug || routeKey;
  }, [settings.locale]);

  /**
   * Get a localized route path WITHOUT location prefix (for global routes)
   */
  const getGlobalPath = useCallback((routeKey: string): string => {
    return `/${routeKey}`;
  }, []);

  /**
   * Change language with identity-based translation (race-condition safe)
   * 
   * Flow (INV-4):
   * 1. Capture current entity's sourceId BEFORE locale change
   * 2. Set pending language switch in context
   * 3. Update settings + i18n
   * 4. DeferredLanguageNavigator processes switch
   */
  const changeLanguage = useCallback(async (
    locale: string,
    displayName?: string
  ): Promise<void> => {
    // Guard: Block language switch during async entity resolution (INV-8)
    if (routeEntityContext?.isResolvingEntity) {
      logger.warn('[useLocalization] Blocked language switch during entity resolution');
      return;
    }
    
    const [language, country] = locale.split('-');
    const pathname = routerLocation.pathname;
    const segments = pathname.split('/').filter(Boolean);
    
    // FAST PATH: Fixed routes don't need URL translation
    const FIXED_ROUTE_PREFIXES = ['account', 'admin', 'auth', 'expert'];
    const isFixedRoute = segments.length > 0 && FIXED_ROUTE_PREFIXES.includes(segments[0]);
    
    if (isFixedRoute) {
      // Instant path: just update settings and i18n, no URL changes needed
      await updateSettingsWithoutI18n({
        locale,
        language,
        country,
        displayName: displayName || locale,
      });
      await i18n.changeLanguage(locale);
      return;
    }
    
    // IDENTITY-BASED TRANSLATION (INV-4):
    // 1. Capture entity info BEFORE locale change
    const currentEntity = routeEntityContext?.currentEntity;
    const entityType = currentEntity?.type || 'dynamic';
    const entitySourceId = currentEntity?.sourceId || null;
    
    if (process.env.NODE_ENV === 'development') {
      logger.debug('[useLocalization] changeLanguage with identity', {
        targetLocale: locale,
        entityType,
        entitySourceId,
        currentPath: pathname,
      });
    }
    
    // 2. Set pending language switch (will be processed by DeferredLanguageNavigator)
    if (routeEntityContext) {
      // Extract metadata based on entity type
      let dynamicMetadata: {
        topicSlug?: string;
        topicId?: string;
        topicsPrefix?: string;
        isPublication?: boolean;
        publicationId?: string;
        authorSlug?: string;
        publicationSlug?: string;
      } | undefined;

      // Check if this is a publication (INV-9 revised)
      if (entityType === 'dynamic' && currentEntity?.metadata?.isPublication) {
        dynamicMetadata = {
          isPublication: true,
          publicationId: currentEntity.metadata.publicationId,
          authorSlug: currentEntity.metadata.authorSlug,
          publicationSlug: currentEntity.metadata.publicationSlug,
        };
      }
      // Check if this is a topic page (INV-7)
      else if (entityType === 'dynamic' && currentEntity?.metadata?.topicSlug) {
        dynamicMetadata = {
          topicSlug: currentEntity.metadata.topicSlug,
          topicId: currentEntity.metadata.topicId ?? currentEntity.sourceId ?? undefined,
          topicsPrefix: segments[0],
        };
      }
      // Legacy fallback for topic detection
      else if (entityType === 'dynamic' && segments.length >= 2) {
        const potentialTopicSlug = segments[1];
        if (potentialTopicSlug) {
          dynamicMetadata = {
            topicSlug: potentialTopicSlug,
            topicsPrefix: segments[0],
          };
        }
      }
      
      routeEntityContext.setPendingLanguageSwitch({
        targetLocale: locale,
        entityType,
        entitySourceId,
        displayName,
        createdAt: Date.now(),
        metadata: dynamicMetadata,
      });
    }
    
    // 3. Update settings
    await updateSettingsWithoutI18n({
      locale,
      language,
      country,
      displayName: displayName || locale,
    });
    
    // 4. Change i18n language (loads translations)
    await i18n.changeLanguage(locale);
    
    // 5. Navigation is deferred to DeferredLanguageNavigator
  }, [routerLocation.pathname, routeEntityContext, updateSettingsWithoutI18n]);

  return {
    // Language info
    locale: settings.locale,
    language: settings.language,
    isReady: true, // DB-first is always ready
    
    // Path generation - DEPRECATED: Use useLocalizedRoutes() for localized paths
    getPath, // Deprecated - only returns raw route key, not localized
    getGlobalPath,
    getHomePath,
    getLocalizedRouteSlug: (key: string) => key, // Sync fallback
    findRouteKey: (path: string) => path.replace(/^\//, ''), // Simple sync version
    
    // Language change (race-condition safe)
    changeLanguage,
    navigateToLocalizedPath: async () => {}, // Deprecated - handled by DeferredLanguageNavigator
    
    // Location context
    locationSlug,
    locationLanguage,
    isLocationContext: !!locationSlug,
    
    // Formatting passthrough from LocaleContext
    formatDate,
    formatNumber,
    formatCurrency,
  };
};
